import { memo } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog"
import { Button } from "@/app/components/ui/button"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SectionArchiveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sectionTitle: string
  unfinishedCount: number
  onArchiveSectionOnly: () => void
  onArchiveWithTasks: () => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Confirmation dialog when archiving a section that has unfinished tasks.
 *
 * Three actions:
 *   - Cancel (close dialog)
 *   - "Just section" — archive section only, tasks become "loose"
 *   - "Section and X tasks" — archive section + all unfinished children
 *
 * v1 pattern: inline Dialog in CardPreviewSection.
 */
export const SectionArchiveDialog = memo(function SectionArchiveDialog({
  open,
  onOpenChange,
  sectionTitle,
  unfinishedCount,
  onArchiveSectionOnly,
  onArchiveWithTasks,
}: SectionArchiveDialogProps) {
  const { t } = useTranslation()

  const title = sectionTitle.trim() || t("Untitled")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{t("Archive all?")}</DialogTitle>
          <DialogDescription>
            {t(
              "Do you want to archive all items under {{title}} or just the section itself?",
              { title },
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex gap-2 sm:justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="secondary" onClick={onArchiveWithTasks}>
            {t("Section and {{count}} tasks", { count: unfinishedCount })}
          </Button>
          <Button variant="primary" onClick={onArchiveSectionOnly}>
            {t("Just section")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
})
